/**
 * Formatação do Dashboard de Reviews.
 *
 * Ao contrário do Painel de Custos, aqui os números são contagens inteiras — não há
 * `numeric` do Postgres trafegando como string, então não há a cautela de nunca passar
 * por `Number` que `costs-format.util.ts` documenta. `Number.toLocaleString` é seguro
 * para tudo que este módulo formata.
 */

const EMPTY_LABEL = '—';

/** Contagem com separador de milhar (pt-BR). */
export function formatCount(value: number | undefined | null): string {
  if (value == null || !Number.isFinite(value)) { return EMPTY_LABEL; }
  return value.toLocaleString('pt-BR');
}

/**
 * Decimal que o backend manda como texto ("22.9", "11.09") no formato brasileiro, com uma
 * casa: "22,9", "11,1". Texto que não é número passa como veio.
 */
export function formatDecimal(raw: string | undefined | null): string {
  if (raw == null || raw === '') { return EMPTY_LABEL; }
  const value = Number(raw);
  if (!Number.isFinite(value)) { return raw; }
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
}

/**
 * Anexa a unidade a um valor já formatado — "22,9" vira "22,9 achados". Preserva `—`
 * como veio: sem valor, a unidade só confundiria mais ("— achados" não diz nada).
 */
export function withUnit(formatted: string, unit: string): string {
  return formatted === EMPTY_LABEL ? formatted : `${formatted} ${unit}`;
}

/** Percentual inteiro de `part` sobre `total`. `—` sem base (total zero ou ausente). */
export function formatSharePercent(part: number | undefined | null, total: number | undefined | null): string {
  if (!total || !Number.isFinite(total) || part == null || !Number.isFinite(part)) { return EMPTY_LABEL; }
  return `${Math.round((part / total) * 100)}%`;
}

/**
 * Segundos para um rótulo curto: `"45s"`, `"4min"`, `"2h 15min"`, `"3d"` — a unidade
 * troca ao completar a seguinte, então 96s já sai como `"2min"`.
 *
 * O backend nunca manda fração de segundo (a agregação é `EXTRACT(EPOCH ...)::bigint`
 * no SQL), então não há cuidado de arredondamento aqui — só escolha de unidade.
 */
export function formatDurationSeconds(seconds: number | undefined | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) { return EMPTY_LABEL; }

  if (seconds < 60) { return `${Math.round(seconds)}s`; }

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) { return `${minutes}min`; }

  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) { return remMinutes ? `${hours}h ${remMinutes}min` : `${hours}h`; }

  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours ? `${days}d ${remHours}h` : `${days}d`;
}

/**
 * Variação de uma métrica contra a janela anterior, pronta para o `po-tag`: o rótulo
 * já formatado, e o sentido (`up`) separado da cor — quem exibe decide se subir é bom
 * (reviews, conclusão) ou ruim (falhas), porque isso não é propriedade do número.
 */
export interface CountVariation {
  label: string;
  up: boolean;
  flat: boolean;
}

/**
 * Variação percentual entre duas contagens. Espelha `costVariation` do Painel de
 * Custos, sobre `number` em vez da string de dinheiro.
 *
 * Devolve `null` sem base de comparação: com `previous` zero toda alta seria "infinito
 * por cento" — ir de 0 para 3 não é +300%, é o primeiro dado —, e sem a janela
 * anterior carregada não há o que comparar. Nos dois casos o card simplesmente não
 * mostra o tag.
 */
export function countVariation(
  current: number | undefined | null,
  previous: number | undefined | null
): CountVariation | null {
  if (previous == null || !Number.isFinite(previous) || previous === 0) { return null; }
  if (current == null || !Number.isFinite(current)) { return null; }

  const pct = Math.round(((current - previous) / previous) * 100);
  return { label: `${pct > 0 ? '+' : ''}${pct}%`, up: pct > 0, flat: pct === 0 };
}

/**
 * Variação entre duas taxas — em PONTOS percentuais, nunca em "% de %".
 *
 * Uma taxa de conclusão que vai de 90% para 95% subiu 5 pontos; dizer "+5,6%" (a
 * variação relativa) descreve o mesmo fato de um jeito que ninguém confere de cabeça.
 * As entradas são frações (0..1), não percentuais já multiplicados — é o que `share`
 * devolve.
 */
export function rateVariation(current: number | null, previous: number | null): CountVariation | null {
  if (current == null || previous == null || !Number.isFinite(current) || !Number.isFinite(previous)) { return null; }

  const points = Math.round((current - previous) * 100);
  return { label: `${points > 0 ? '+' : ''}${points} p.p.`, up: points > 0, flat: points === 0 };
}

/** Fração de `part` sobre `total`, ou `null` sem base — a entrada de `rateVariation`. */
export function share(part: number | undefined | null, total: number | undefined | null): number | null {
  if (!total || !Number.isFinite(total) || part == null || !Number.isFinite(part)) { return null; }
  return part / total;
}

/** `YYYY-MM-DD` → `DD/MM/YYYY`. Sem `new Date`, que recuaria um dia em UTC-3. */
export function formatApiDate(raw: string | undefined | null): string {
  const value = (raw ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) { return value || EMPTY_LABEL; }
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
}

/** `DD/MM` — rótulo curto do eixo X da série diária. */
export function formatApiDateShort(raw: string | undefined | null): string {
  const full = formatApiDate(raw);
  return full.length === 10 ? full.slice(0, 5) : full;
}
